// Centralized API client with cookie credentials.
//
// Origin handling — critical for custom-domain deployments:
// The frontend is built once and the env-baked REACT_APP_BACKEND_URL may
// point to a DIFFERENT host than the one the user is browsing on
// (e.g. preview vs. the custom production domain bamediaperformance.de).
// When that happens, cookies set by the API host are *cross-site* and modern
// browsers (Safari ITP, Firefox Strict, Chrome 3rd-party blocking) will not
// persist them — login appears to succeed but the next request is anonymous.
//
// Fix: at runtime, compare the env origin with the page's origin. If they
// match → use the env URL (full). If they differ → use a relative `/api`
// path which always hits the SAME origin as the page, making cookies
// first-party.
//
// This is purely a runtime adaptation; we never mutate the env variable
// itself, so preview / dev behaviour is identical.
import axios from "axios";

function resolveBackendBase() {
    const env = (process.env.REACT_APP_BACKEND_URL || "").trim();
    if (typeof window === "undefined") return env;
    try {
        if (env) {
            const envOrigin = new URL(env).origin;
            if (envOrigin === window.location.origin) return env;
        }
    } catch {
        // env URL is invalid — fall through to same-origin
    }
    // Same-origin mode → relative API path
    return window.location.origin;
}

export const BACKEND_URL = resolveBackendBase();
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Unbekannter Fehler. Bitte erneut versuchen.";
    if (typeof detail === "string") return _stripPydanticPrefix(detail);
    if (Array.isArray(detail))
        return detail
            .map((e) =>
                e && typeof e.msg === "string" ? _stripPydanticPrefix(e.msg) : JSON.stringify(e),
            )
            .filter(Boolean)
            .join(" · ");
    if (detail && typeof detail.msg === "string") return _stripPydanticPrefix(detail.msg);
    return String(detail);
}

// Pydantic v2 wraps validator errors with "Value error, " — strip that
// prefix so end-users see clean German messages.
function _stripPydanticPrefix(msg) {
    return String(msg).replace(/^(Value error,|Assertion failed,)\s*/i, "");
}

export default api;
